import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  where,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../firebase';

export const WEEK_DAYS = [
  { id: 'monday', label: 'Lunes' },
  { id: 'tuesday', label: 'Martes' },
  { id: 'wednesday', label: 'Miércoles' },
  { id: 'thursday', label: 'Jueves' },
  { id: 'friday', label: 'Viernes' },
  { id: 'saturday', label: 'Sábado' },
  { id: 'sunday', label: 'Domingo' },
];

export const createDefaultSchedule = () =>
  WEEK_DAYS.reduce((schedule, day, index) => {
    schedule[day.id] = {
      enabled: index < 5,
      intervals: index < 5 ? [{ start: '09:00', end: '18:00' }] : [],
    };
    return schedule;
  }, {});

export const normalizeEmployeeEmail = (email) => email.trim().toLowerCase();

export const subscribeToEmployees = (onNext, onError) => {
  const employeesQuery = query(
    collection(db, 'employeeAvailability'),
    orderBy('email')
  );

  return onSnapshot(
    employeesQuery,
    (snapshot) => {
      onNext(snapshot.docs.map((employee) => ({
        id: employee.id,
        ...employee.data(),
      })));
    },
    onError
  );
};

export const saveEmployee = async (employee, userId, { createOnly = false } = {}) => {
  const email = normalizeEmployeeEmail(employee.email);
  const employeeRef = doc(db, 'employeeAvailability', email);

  await runTransaction(db, async (transaction) => {
    const existing = await transaction.get(employeeRef);
    if (createOnly && existing.exists()) {
      const error = new Error('An employee with this email already exists.');
      error.code = 'employee-exists';
      throw error;
    }
    const createdAt = existing.exists()
      ? existing.data().createdAt
      : serverTimestamp();

    transaction.set(employeeRef, {
      email,
      displayName: employee.displayName.trim(),
      timezone: employee.timezone || 'Europe/Madrid',
      employmentStartDate: employee.employmentStartDate || '',
      employmentEndDate: employee.employmentEndDate || '',
      weeklySchedule: employee.weeklySchedule,
      createdAt,
      updatedAt: serverTimestamp(),
      updatedBy: userId,
    });
  });
};

export const removeEmployee = async (email) => {
  const normalizedEmail = normalizeEmployeeEmail(email);
  const vacationsQuery = query(
    collection(db, 'employeeVacations'),
    where('employeeEmail', '==', normalizedEmail),
    limit(450)
  );

  let vacationSnapshot = await getDocs(vacationsQuery);
  while (!vacationSnapshot.empty) {
    const batch = writeBatch(db);
    vacationSnapshot.docs.forEach((vacation) => batch.delete(vacation.ref));
    await batch.commit();
    vacationSnapshot = await getDocs(vacationsQuery);
  }

  await deleteDoc(doc(db, 'employeeAvailability', normalizedEmail));
};

export const subscribeToVacations = (fromDate, onNext, onError) => {
  const vacationsQuery = query(
    collection(db, 'employeeVacations'),
    where('date', '>=', fromDate),
    orderBy('date')
  );

  return onSnapshot(
    vacationsQuery,
    (snapshot) => {
      onNext(snapshot.docs.map((vacation) => ({
        id: vacation.id,
        ...vacation.data(),
      })));
    },
    onError
  );
};

const getDatesInRange = (startDate, endDate) => {
  const dates = [];
  const current = new Date(`${startDate}T00:00:00Z`);
  const end = new Date(`${endDate}T00:00:00Z`);

  while (current <= end) {
    dates.push(current.toISOString().slice(0, 10));
    current.setUTCDate(current.getUTCDate() + 1);
  }

  return dates;
};

export const saveVacationRange = async ({
  employeeEmail,
  startDate,
  endDate,
  userId,
}) => {
  const email = normalizeEmployeeEmail(employeeEmail);
  const dates = getDatesInRange(startDate, endDate);

  if (dates.length === 0 || dates.length > 366) {
    throw new Error('Vacation ranges must contain between 1 and 366 days.');
  }

  const batch = writeBatch(db);

  dates.forEach((date) => {
    const vacationId = `${encodeURIComponent(email)}__${date}`;
    batch.set(doc(db, 'employeeVacations', vacationId), {
      employeeEmail: email,
      date,
      available: false,
      intervals: [],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      updatedBy: userId,
    });
  });

  await batch.commit();
};

export const removeVacationDay = (employeeEmail, date) =>
  deleteDoc(doc(
    db,
    'employeeVacations',
    `${encodeURIComponent(normalizeEmployeeEmail(employeeEmail))}__${date}`
  ));

export const removeVacationRange = async (employeeEmail, startDate, endDate) => {
  const email = normalizeEmployeeEmail(employeeEmail);
  const dates = getDatesInRange(startDate, endDate);
  const batch = writeBatch(db);

  dates.forEach((date) => {
    batch.delete(doc(
      db,
      'employeeVacations',
      `${encodeURIComponent(email)}__${date}`
    ));
  });

  await batch.commit();
};
